import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api, ErreurApi, type Bascule, type Moi } from './api';
import type { EtatFiltres } from './Filtres';
import { IcAccueil, IcFermer, IcJournal, IcMenu, IcSortie, IcTableau, IcVerrou } from './icones';
import { Journal } from './Journal';
import { TableauDeBord } from './TableauDeBord';

type Ecran = 'accueil' | 'tableau' | 'journal';
const lireEcran = (): Ecran => (location.hash === '#/tableau' ? 'tableau' : location.hash === '#/journal' ? 'journal' : 'accueil');

/** Modules dans l'ordre des lots du cadrage. */
const MODULES: { cle: string; libelle: string; lot: number; ecran?: Ecran }[] = [
  { cle: 'tableau_de_bord', libelle: 'Tableau de bord et journal', lot: 1, ecran: 'tableau' },
  { cle: 'rendu_mails', libelle: 'Rendu des mails', lot: 2 },
  { cle: 'contacts', libelle: 'Contacts, listes, import', lot: 3 },
  { cle: 'campagnes', libelle: 'Campagnes', lot: 4 },
  { cle: 'envoi', libelle: "Boîtes et moteur d'envoi", lot: 5 },
  { cle: 'relances', libelle: 'Relances', lot: 6 },
  { cle: 'reponses', libelle: 'Réponses', lot: 7 },
  { cle: 'rgpd', libelle: 'RGPD', lot: 8 },
];

function Marque({ clair = false }: { clair?: boolean }) {
  return (
    <span className={`marque ${clair ? 'clair' : ''}`}>
      <svg className="marque-signe" viewBox="0 0 32 32" aria-hidden="true">
        <rect x="2" y="7" width="28" height="18" rx="4" />
        <path d="M2.8 9.5 16 18l13.2-8.5" />
        <circle cx="26" cy="8" r="4.5" />
      </svg>
      <span className="marque-nom">MaProspection</span>
    </span>
  );
}

function Connexion({ surConnexion }: { surConnexion: () => void }) {
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      await api.connexion(email, motDePasse);
      surConnexion();
    } catch (err) {
      setErreur(
        err instanceof ErreurApi && err.statut === 429
          ? 'Trop de tentatives. Réessayez dans quelques minutes.'
          : err instanceof ErreurApi && err.statut === 401
            ? 'Identifiant ou mot de passe incorrect.'
            : 'Connexion impossible pour le moment.',
      );
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="connexion">
      <aside className="connexion-cote" aria-hidden="true">
        <Marque clair />
        <p className="connexion-accroche">Chaque campagne, de l'envoi à la réponse, au même endroit.</p>
        <svg className="connexion-courbe" viewBox="0 0 400 160" preserveAspectRatio="none">
          <path d="M0 40 C 90 40, 120 70, 200 78 S 320 104, 400 112 L400 160 L0 160 Z" />
          <path d="M0 40 C 90 40, 120 70, 200 78 S 320 104, 400 112" className="ligne" />
        </svg>
      </aside>
      <main className="connexion-forme">
        <form onSubmit={soumettre} aria-label="Connexion">
          <h1>MaProspection</h1>
          <p className="aide">Connectez-vous avec votre compte habituel du portail de prospection.</p>
          <label>
            Adresse e-mail
            <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>
            Mot de passe
            <input type="password" autoComplete="current-password" required value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} />
          </label>
          {erreur && <p className="erreur" role="alert">{erreur}</p>}
          <button type="submit" className="principal" disabled={envoi}>{envoi ? 'Connexion…' : 'Se connecter'}</button>
        </form>
      </main>
    </div>
  );
}

function Accueil({ moi, version }: { moi: Moi; version: number }) {
  const [modules, setModules] = useState<Bascule[] | null>(null);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    api.bascule().then((r) => setModules(r.modules)).catch(() => setErreur(true));
  }, [version]);

  const moteur = (cle: string) => modules?.find((m) => m.module === cle)?.moteur;
  const liste = modules ? MODULES.filter((m) => modules.some((x) => x.module === m.cle)) : [];

  return (
    <>
      <header className="entete-page">
        <h1>Bonjour {moi.utilisateur.nom}</h1>
        <p className="aide">
          La nouvelle application arrive module par module. Tant qu'un module est marqué « Ancien portail », continuez à
          l'utiliser à l'endroit habituel.
        </p>
      </header>
      <section aria-label="Avancement des modules" className="panneau">
        <h2>Avancement</h2>
        {erreur && <p className="erreur" role="alert">Avancement indisponible pour le moment.</p>}
        {!modules && !erreur && <p className="aide">Chargement…</p>}
        <ol className="modules">
          {liste.map((m) => {
            const v2 = moteur(m.cle) === 'v2';
            return (
              <li key={m.cle} className={v2 ? 'v2' : m.ecran ? 'disponible' : ''}>
                <span className="module-lot">Lot {m.lot}</span>
                <span className="module-nom">
                  {m.ecran ? <a href={`#/${m.ecran}`}>{m.libelle}</a> : m.libelle}
                  {m.ecran && !v2 && <span className="aide"> — disponible ici, en comparaison avec l'ancien portail</span>}
                </span>
                <span className={v2 ? 'etat v2' : 'etat'}>{v2 ? 'Nouvelle application' : 'Ancien portail'}</span>
              </li>
            );
          })}
        </ol>
      </section>
    </>
  );
}

function LienMenu({ actif, href, icone, children }: { actif: boolean; href: string; icone: ReactNode; children: ReactNode }) {
  return (
    <a href={href} aria-current={actif ? 'page' : undefined} className={actif ? 'actif' : ''}>
      {icone}
      <span>{children}</span>
    </a>
  );
}

function Cadre({ moi, surDeconnexion }: { moi: Moi; surDeconnexion: () => void }) {
  const [ecran, setEcran] = useState<Ecran>(lireEcran());
  const [filtres, setFiltres] = useState<EtatFiltres>({ campagne: null, preset: '30j', du: '', au: '' });
  const [versionBascule, setVersionBascule] = useState(0);
  const [menuOuvert, setMenuOuvert] = useState(false);
  useEffect(() => {
    const ecouter = () => { setEcran(lireEcran()); setMenuOuvert(false); };
    window.addEventListener('hashchange', ecouter);
    return () => window.removeEventListener('hashchange', ecouter);
  }, []);
  const initiales = moi.utilisateur.nom.split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className={`page ${menuOuvert ? 'menu-ouvert' : ''}`}>
      <div className="barre-mobile">
        <Marque />
        {moi.environnement !== 'production' && <span className="badge-env">{moi.environnement.toUpperCase()}</span>}
        <span className="espace" />
        <button className="icone" aria-label={menuOuvert ? 'Fermer le menu' : 'Ouvrir le menu'} aria-expanded={menuOuvert} onClick={() => setMenuOuvert((x) => !x)}>
          {menuOuvert ? <IcFermer /> : <IcMenu />}
        </button>
      </div>
      <aside className="barre-laterale">
        <div className="barre-tete">
          <Marque clair />
          {moi.environnement !== 'production' && <span className="badge-env" data-testid="badge-env">{moi.environnement.toUpperCase()}</span>}
        </div>
        <nav className="menu" aria-label="Navigation">
          <LienMenu actif={ecran === 'accueil'} href="#/" icone={<IcAccueil />}>Accueil</LienMenu>
          <LienMenu actif={ecran === 'tableau'} href="#/tableau" icone={<IcTableau />}>Tableau de bord</LienMenu>
          <LienMenu actif={ecran === 'journal'} href="#/journal" icone={<IcJournal />}>Journal</LienMenu>
        </nav>
        <div className="menu-avenir" aria-label="Modules à venir">
          <p>À venir</p>
          <ul>
            {MODULES.filter((m) => !m.ecran).map((m) => (
              <li key={m.cle}><IcVerrou /><span>{m.libelle}</span><span className="lot">lot {m.lot}</span></li>
            ))}
          </ul>
        </div>
        <div className="utilisateur">
          <span className="avatar" aria-hidden="true">{initiales}</span>
          <span className="utilisateur-nom">{moi.utilisateur.nom}<span>{moi.utilisateur.role === 'admin' ? 'Administrateur' : 'Utilisateur'}</span></span>
          <button className="icone" title="Se déconnecter" aria-label="Se déconnecter" onClick={() => api.deconnexion().finally(surDeconnexion)}><IcSortie /></button>
        </div>
      </aside>
      <main className="contenu">
        {ecran === 'accueil' && <Accueil moi={moi} version={versionBascule} />}
        {ecran === 'tableau' && (
          <TableauDeBord moi={moi} filtres={filtres} surFiltres={setFiltres} surBascule={() => setVersionBascule((v) => v + 1)}
            versCampagne={(id) => setFiltres((f) => ({ ...f, campagne: id }))} />
        )}
        {ecran === 'journal' && <Journal filtres={filtres} surFiltres={setFiltres} />}
        <footer>Version {moi.version}</footer>
      </main>
    </div>
  );
}

export function App() {
  const [moi, setMoi] = useState<Moi | null | undefined>(undefined);
  const charger = () => api.moi().then(setMoi).catch(() => setMoi(null));
  useEffect(() => {
    void charger();
  }, []);
  if (moi === undefined) return <p className="aide centre">Chargement…</p>;
  if (moi === null) return <Connexion surConnexion={() => void charger()} />;
  return <Cadre moi={moi} surDeconnexion={() => setMoi(null)} />;
}
